import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Banner, Button, IconButton, LoadingSpinner, MenuV2, PopoverPrimitive, Text } from '@figma/fpl-components';
import {
  Icon16Close,
  Icon16Image,
  Icon24ApprovedCheckmark,
  Icon24Check,
  Icon24Ellipse,
  Icon24Image,
  Icon24Plus,
  Icon24Upload,
  Icon24VideoLarge,
  Icon24Voice,
} from '@figma/fpl-icons';
import { ColorTheme } from '@figma/fpl-themes';
import { borderRadius, color, shadow, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { ToolOutputThumbnail } from '@/components/Tool/ToolOutputThumbnail';
import { I18N_KEYS } from '@/language/keys';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { MenuTriggerTooltip } from './MenuTrigger';
import { mediaTypeOf, typesFor, type ComposerInput, type InputSlot, type ReferenceRoom } from './inputRules';
import { useSlotDrop } from './useMediaDrop';
import type { TFunction } from 'i18next';
import type { GenerateInputs } from '@/api/generate.api';
import type { MediaAsset } from '@/types/api/assets';
import type { InputNotice, InputTarget } from './useComposerInputs';

/** How the limit copy names what it counts. "References" where video or audio count too. */
export const limitWord = (inputs: GenerateInputs): 'images' | 'references' =>
  inputs.references?.types.every((type) => type === 'image') ? 'images' : 'references';

type BannerContent = { variant: 'default' | 'danger'; title?: string; message: string };

const KEYS = I18N_KEYS.GENERATE_PAGE;

/** The words for the types a model takes, as the wrong-type message lists them. */
function typesText(inputs: GenerateInputs, t: TFunction): string {
  const types = inputs.references?.types ?? ['image'];
  if (types.includes('audio')) {
    return t(KEYS.TYPES_MEDIA);
  }
  return t(types.includes('video') ? KEYS.TYPES_IMAGES_AND_VIDEOS : KEYS.TYPES_IMAGES);
}

function noticeContent(notice: InputNotice, inputs: GenerateInputs, t: TFunction): BannerContent {
  const max = inputs.references?.max;
  const maxVideos = inputs.references?.maxByType?.video;
  const images = limitWord(inputs) === 'images';
  switch (notice.type) {
    case 'limit':
      // The file picker can't refuse files, so too many picked there is an error. A drop just doesn't land.
      if (notice.fromPicker) {
        const tryLess =
          notice.limit === 'video'
            ? t(KEYS.TRY_FEWER_VIDEOS, { max: maxVideos })
            : t(images ? KEYS.TRY_FEWER_IMAGES : KEYS.TRY_FEWER_FILES, { max });
        return { variant: 'danger', title: t(KEYS.MAX_UPLOAD_REACHED), message: tryLess };
      }
      if (notice.limit === 'video') {
        return {
          variant: 'default',
          title: t(KEYS.VIDEO_MAX_REACHED),
          message: t(KEYS.UP_TO_VIDEOS, { max: maxVideos }),
        };
      }
      return {
        variant: 'default',
        title: t(images ? KEYS.IMAGE_MAX_REACHED : KEYS.REFERENCE_MAX_REACHED),
        message: t(images ? KEYS.UP_TO_IMAGES : KEYS.UP_TO_REFERENCES, { max }),
      };
    case 'uploadFailed':
      return { variant: 'default', message: t(I18N_KEYS.GENERAL.UPLOAD_ERROR) };
    case 'wrongType':
      return {
        variant: 'default',
        message: notice.formats
          ? t(KEYS.WRONG_IMAGE_FORMAT, { count: notice.count })
          : t(KEYS.WRONG_TYPE, { count: notice.count, types: typesText(inputs, t) }),
      };
    case 'frameTakesOne':
      return { variant: 'default', message: t(KEYS.FRAME_TAKES_ONE, { count: notice.count }) };
    case 'notTaken':
      return {
        variant: 'default',
        title: t(KEYS.INPUTS_REMOVED),
        message: t(KEYS.MODEL_DOESNT_TAKE, { model: notice.modelName }),
      };
  }
}

/**
 * The latest message about inputs, over the prompt box. While only a last frame is attached and no other message
 * shows, it asks for a first frame, and that one can't be closed.
 */
export function InputBanner({
  notice,
  firstFrameNeeded,
  inputs,
  onDismiss,
}: {
  notice: InputNotice | null;
  firstFrameNeeded: boolean;
  inputs: GenerateInputs;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const content: BannerContent | null = notice
    ? noticeContent(notice, inputs, t)
    : firstFrameNeeded
      ? { variant: 'default', title: t(KEYS.FIRST_FRAME_NEEDED), message: t(KEYS.ADD_FIRST_FRAME_FIRST) }
      : null;
  if (!content) {
    return null;
  }
  return (
    <div {...stylex.props(styles.banner)}>
      <Banner.Inset variant={content.variant} onDismiss={notice ? onDismiss : undefined}>
        <Banner.Message title={content.title}>{content.message}</Banner.Message>
      </Banner.Inset>
    </div>
  );
}

/** The open add panel: its slot, and whether the footer's + button or the slot in the row opened it. */
export type OpenPanel = { slot: InputSlot; anchor: 'row' | 'footer' };

/** What every add panel needs, wherever it opens from. */
type PanelProps = {
  inputs: GenerateInputs;
  room: ReferenceRoom;
  /** Results on the page the panel offers. */
  gallery: readonly MediaAsset[];
  attached: readonly ComposerInput[];
  openPanel: OpenPanel | null;
  onOpenPanel: (panel: OpenPanel | null) => void;
  /** Signed-out visitors are sent to sign in instead of opening a panel. */
  onSignIn?: () => void;
  onConfirm: (assets: readonly MediaAsset[], slot: InputSlot) => void;
  onUpload: (slot: InputSlot) => void;
  /** A drop on one slot, which then gets what was dropped. */
  onDropInto?: (dataTransfer: DataTransfer, target: InputTarget) => void;
};

const maxReachedText = (inputs: GenerateInputs, t: TFunction) =>
  t(limitWord(inputs) === 'images' ? KEYS.MAX_IMAGES_REACHED : KEYS.MAX_REFERENCES_REACHED, {
    max: inputs.references?.max,
  });

/**
 * The frame slots and reference thumbnails over the prompt. Each empty slot, and "+ Reference", opens the add
 * panel above itself. "+ Reference" goes away once no place is left, as in the design.
 */
export function InputRow({ onRemove, ...panel }: PanelProps & { onRemove: (input: ComposerInput) => void }) {
  const { t } = useTranslation();
  const { inputs, attached, room } = panel;
  if (!inputs.frames && !inputs.references) {
    return null;
  }
  const frameOf = (slot: InputSlot) => attached.find((input) => input.slot === slot);
  const references = attached.filter((input) => input.slot === 'reference');
  const full = room.total <= 0;
  // Next to references a filled first frame looks like one of them, so it says what it is.
  const captionFrames = Boolean(inputs.references);

  return (
    <div {...stylex.props(styles.row)}>
      {inputs.frames && (
        <FrameSlot
          {...panel}
          slot="firstFrame"
          label={t(KEYS.FIRST_FRAME)}
          tooltip={t(KEYS.ADD_FIRST_FRAME)}
          frame={frameOf('firstFrame')}
          caption={captionFrames}
          // next to references, the first frame takes one of their places
          disabledReason={inputs.references && full && !frameOf('firstFrame') ? maxReachedText(inputs, t) : undefined}
          onRemove={onRemove}
        />
      )}
      {inputs.frames?.last && (
        <FrameSlot
          {...panel}
          slot="lastFrame"
          label={t(KEYS.LAST_FRAME)}
          tooltip={t(KEYS.ADD_LAST_FRAME)}
          frame={frameOf('lastFrame')}
          caption={captionFrames}
          onRemove={onRemove}
        />
      )}
      {references.map((input, index) => (
        <InputTile
          key={input.id}
          input={input}
          name={t(KEYS.REFERENCE_NAME, { index: index + 1 })}
          onRemove={() => onRemove(input)}
        />
      ))}
      {inputs.references && !full && (
        <PanelAnchor {...panel} slot="reference" anchor="row">
          {(triggerProps) => (
            <DropTarget slot="reference" onDropInto={panel.onDropInto}>
              <Tooltip title={t(KEYS.ATTACH)}>
                <button type="button" {...triggerProps} {...stylex.props(styles.emptySlot)}>
                  <Icon24Plus />
                  <Text.span variant="body-small" color="secondary">
                    {t(KEYS.REFERENCE_SLOT)}
                  </Text.span>
                </button>
              </Tooltip>
            </DropTarget>
          )}
        </PanelAnchor>
      )}
    </div>
  );
}

/**
 * The footer's + button. On a model with references it opens the add panel above itself, as in the design. On
 * a frames model it asks which frame to add, then opens that slot's panel in the row.
 */
export function AddButton(panel: PanelProps) {
  const { t } = useTranslation();
  const { inputs, room } = panel;
  if (inputs.references) {
    const full = room.total <= 0;
    return (
      <PanelAnchor {...panel} slot="reference" anchor="footer">
        {(triggerProps, open) => (
          <MenuTriggerTooltip title={full ? maxReachedText(inputs, t) : t(KEYS.ATTACH)} menuOpen={open}>
            {/* a disabled button gets no pointer events, so the span keeps the tooltip working */}
            <span {...stylex.props(styles.tooltipAnchor)}>
              <IconButton aria-label={t(KEYS.ATTACH)} disabled={full} {...triggerProps}>
                <Icon24Plus />
              </IconButton>
            </span>
          </MenuTriggerTooltip>
        )}
      </PanelAnchor>
    );
  }
  return inputs.frames ? <AddFrameMenu {...panel} /> : null;
}

function AddFrameMenu({ inputs, onOpenPanel, onSignIn }: PanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { manager, getTriggerProps } = MenuV2.useMenu({ open, onOpenChange: setOpen });
  const openFrame = (slot: InputSlot) => onOpenPanel({ slot, anchor: 'row' });

  if (onSignIn) {
    return (
      <Tooltip title={t(KEYS.ATTACH)}>
        <IconButton aria-label={t(KEYS.ATTACH)} onClick={onSignIn}>
          <Icon24Plus />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <>
      <MenuTriggerTooltip title={t(KEYS.ATTACH)} menuOpen={open}>
        <IconButton aria-label={t(KEYS.ATTACH)} {...getTriggerProps()}>
          <Icon24Plus />
        </IconButton>
      </MenuTriggerTooltip>
      <MenuV2.Root
        manager={manager}
        aria-label={t(KEYS.ATTACH)}
        position={{ area: 'top span-right' }}
        colorTheme={{ brand: 'weave', mode: 'dark' }}
      >
        <MenuV2.Item onClick={() => openFrame('firstFrame')}>{t(KEYS.FIRST_FRAME)}</MenuV2.Item>
        {inputs.frames?.last && <MenuV2.Item onClick={() => openFrame('lastFrame')}>{t(KEYS.LAST_FRAME)}</MenuV2.Item>}
      </MenuV2.Root>
    </>
  );
}

/** Opens the add panel for `slot` above its trigger. */
function PanelAnchor({
  slot,
  anchor,
  children,
  ...panel
}: PanelProps & {
  slot: InputSlot;
  anchor: OpenPanel['anchor'];
  children: (triggerProps: Record<string, unknown>, open: boolean) => ReactNode;
}) {
  const { t } = useTranslation();
  const { openPanel, onOpenPanel, onSignIn } = panel;
  const open = openPanel?.slot === slot && openPanel.anchor === anchor;
  const { getTriggerProps, getContainerProps } = PopoverPrimitive.usePopover({
    isOpen: open,
    onOpenChange: (next: boolean) => onOpenPanel(next ? { slot, anchor } : null),
    type: 'dialog',
    softDismiss: true,
    placement: 'top-start',
    offset: 8,
  });
  const triggerProps = onSignIn ? { onClick: onSignIn } : getTriggerProps();

  return (
    <>
      {children(triggerProps, open)}
      <PopoverPrimitive.Container {...getContainerProps({ role: 'dialog', 'aria-label': t(KEYS.ATTACH) })}>
        <ColorTheme brand="weave" mode="dark">
          <AddPanel {...panel} slot={slot} onClose={() => onOpenPanel(null)} />
        </ColorTheme>
      </PopoverPrimitive.Container>
    </>
  );
}

function FrameSlot({
  label,
  tooltip,
  frame,
  caption,
  disabledReason,
  onRemove,
  ...panel
}: PanelProps & {
  slot: InputSlot;
  label: string;
  tooltip: string;
  frame: ComposerInput | undefined;
  caption: boolean;
  disabledReason?: string;
  onRemove: (input: ComposerInput) => void;
}) {
  return (
    <PanelAnchor {...panel} anchor="row">
      {(triggerProps) => (
        <DropTarget slot={panel.slot} onDropInto={panel.onDropInto}>
          {frame ? (
            // a filled frame still opens its panel, to replace it, also from the footer's + menu
            <InputTile
              input={frame}
              name={label}
              caption={caption ? label : undefined}
              triggerProps={triggerProps}
              onRemove={() => onRemove(frame)}
            />
          ) : (
            <Tooltip title={disabledReason ?? tooltip}>
              <span {...stylex.props(styles.tooltipAnchor)}>
                <button
                  type="button"
                  disabled={disabledReason !== undefined}
                  {...triggerProps}
                  {...stylex.props(styles.emptySlot)}
                >
                  <Icon16Image />
                  <Text.span variant="body-small" color="secondary" {...stylex.props(styles.slotLabel)}>
                    {label}
                  </Text.span>
                </button>
              </span>
            </Tooltip>
          )}
        </DropTarget>
      )}
    </PanelAnchor>
  );
}

/** Takes a drop for one slot and shows when a drag is over it, so a drop can fill or replace a chosen slot. */
function DropTarget({
  slot,
  onDropInto,
  children,
}: {
  slot: InputSlot;
  onDropInto: PanelProps['onDropInto'];
  children: ReactNode;
}) {
  const { over, dropProps } = useSlotDrop(slot, onDropInto);
  return (
    <div {...dropProps} {...stylex.props(styles.dropTarget, over && styles.dropOver)}>
      {children}
    </div>
  );
}

/**
 * The tool page's thumbnail, with a small remove button in its corner that shows on hover or focus. While its file
 * uploads, and after the upload failed, it shows like an agent chat attachment: the file's local picture, dimmed
 * with a spinner while it uploads, and a red border once it failed.
 */
function InputTile({
  input,
  name,
  caption,
  triggerProps,
  onRemove,
}: {
  input: ComposerInput;
  name: string;
  caption?: string;
  triggerProps?: Record<string, unknown>;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const uploading = !input.asset && !input.failed;
  const thumbnail = (
    <>
      {input.asset ? (
        <ToolOutputThumbnail asset={input.asset} selected />
      ) : (
        <LocalPreview input={input} dimmed={uploading} />
      )}
      {uploading && (
        <span {...stylex.props(styles.spinner)}>
          <LoadingSpinner size="sm" />
        </span>
      )}
      {caption && (
        <Text.span variant="body-small" {...stylex.props(styles.caption)}>
          {caption}
        </Text.span>
      )}
    </>
  );
  return (
    <div aria-busy={uploading} {...stylex.props(styles.tile, input.failed && styles.tileFailed)}>
      {triggerProps ? (
        <button type="button" aria-label={name} {...triggerProps} {...stylex.props(styles.tileContent)}>
          {thumbnail}
        </button>
      ) : (
        <div role="img" aria-label={name} {...stylex.props(styles.tileContent)}>
          {thumbnail}
        </div>
      )}
      <button
        type="button"
        aria-label={t(KEYS.REMOVE_ATTACHMENT, { name })}
        onClick={onRemove}
        {...stylex.props(styles.remove)}
      >
        <Icon16Close />
      </button>
    </div>
  );
}

const TYPE_ICONS = { image: Icon24Image, video: Icon24VideoLarge, audio: Icon24Voice };

/** The file's own picture before it has an asset, or its type icon when the browser can't draw it. */
function LocalPreview({ input, dimmed }: { input: ComposerInput; dimmed: boolean }) {
  const Icon = TYPE_ICONS[input.mediaType];
  const media = stylex.props(styles.localMedia, dimmed && styles.dimmed);
  if (input.previewUrl && input.mediaType === 'video') {
    return <video src={input.previewUrl} muted playsInline preload="metadata" aria-hidden {...media} />;
  }
  if (input.previewUrl) {
    return <img src={input.previewUrl} alt="" draggable={false} {...media} />;
  }
  return (
    <span aria-hidden {...stylex.props(styles.localMedia, styles.localIcon, dimmed && styles.dimmed)}>
      <Icon />
    </span>
  );
}

/**
 * "From this gallery" with the page's results the slot takes, then "Upload a file" and Confirm. References can
 * pick several up to the limit. A frame picks one. Results already attached show as picked and can't be picked
 * again. The marks follow the tool page's example picker: a check on a picked tile, an empty circle on hover.
 */
function AddPanel({
  slot,
  inputs,
  room,
  gallery,
  attached,
  onClose,
  onConfirm,
  onUpload,
}: PanelProps & { slot: InputSlot; onClose: () => void }) {
  const { t } = useTranslation();
  // By id, since the gallery list is rebuilt whenever a run changes while the panel is open.
  const [pickedIds, setPickedIds] = useState<readonly string[]>([]);
  const types = typesFor(inputs, slot);
  const options = gallery.filter((asset) => {
    const mediaType = mediaTypeOf(asset.type);
    return mediaType !== undefined && types.includes(mediaType);
  });
  const picked = pickedIds.flatMap((id) => options.find((asset) => asset.id === id) ?? []);
  // Only this slot's own inputs show as picked, so the same result can still go in another slot.
  const attachedIds = new Set(
    attached.flatMap((input) => (input.slot === slot && input.asset ? [input.asset.id] : [])),
  );
  const several = slot === 'reference';
  const videoRoom = room.byType.video;
  const pickedVideos = picked.filter((asset) => asset.type === 'video').length;
  const totalFull = several && picked.length >= room.total;
  const videosFull = several && videoRoom !== undefined && pickedVideos >= videoRoom;

  const toggle = useCallback(
    (asset: MediaAsset) =>
      setPickedIds((current) => {
        if (current.includes(asset.id)) {
          return current.filter((id) => id !== asset.id);
        }
        return several ? [...current, asset.id] : [asset.id];
      }),
    [several],
  );
  const handleConfirm = useCallback(() => {
    onConfirm(picked, slot);
    onClose();
  }, [onClose, onConfirm, picked, slot]);
  const handleUpload = useCallback(() => {
    onClose();
    onUpload(slot);
  }, [onClose, onUpload, slot]);

  const word = limitWord(inputs);
  const hint = totalFull
    ? t(word === 'images' ? KEYS.PANEL_MAX_IMAGES : KEYS.PANEL_MAX_REFERENCES, {
        max: inputs.references?.max,
      })
    : videosFull
      ? t(KEYS.PANEL_MAX_VIDEOS, { max: inputs.references?.maxByType?.video })
      : undefined;

  return (
    <div {...stylex.props(styles.panel)}>
      {options.length > 0 && (
        <div {...stylex.props(styles.panelBody)}>
          <Text.p variant="body-medium" color="secondary" {...stylex.props(styles.panelText)}>
            {t(KEYS.FROM_GALLERY)}
          </Text.p>
          <div {...stylex.props(styles.grid)}>
            {options.map((asset, index) => {
              const isAttached = attachedIds.has(asset.id);
              const isPicked = isAttached || pickedIds.includes(asset.id);
              const blocked = !isPicked && (totalFull || (asset.type === 'video' && videosFull));
              return (
                <button
                  key={asset.id}
                  type="button"
                  aria-label={t(KEYS.RESULT_NAME, { index: index + 1 })}
                  aria-pressed={isPicked}
                  disabled={isAttached || blocked}
                  onClick={() => toggle(asset)}
                  {...stylex.props(styles.option, blocked && styles.optionBlocked)}
                >
                  <ToolOutputThumbnail asset={asset} selected />
                  <span aria-hidden {...stylex.props(styles.scrim, !isPicked && !blocked && styles.scrimOnHover)} />
                  <span aria-hidden {...stylex.props(styles.mark, !isPicked && styles.markOnHover)}>
                    {isPicked ? <Icon24ApprovedCheckmark /> : <Icon24Ellipse />}
                  </span>
                </button>
              );
            })}
          </div>
          {hint && (
            <Text.p variant="body-small" color="secondary" role="status" {...stylex.props(styles.panelText)}>
              {hint}
            </Text.p>
          )}
        </div>
      )}
      <div {...stylex.props(styles.panelActions, options.length > 0 && styles.panelActionsDivided)}>
        <Button variant="secondary" width="fill" iconPrefix={<Icon24Upload />} onClick={handleUpload}>
          {t(KEYS.UPLOAD_FILE)}
        </Button>
        {options.length > 0 && (
          <Button
            variant="primary"
            width="fill"
            iconPrefix={<Icon24Check />}
            disabled={picked.length === 0}
            onClick={handleConfirm}
          >
            {t(KEYS.CONFIRM)}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The tool page's thumbnail size, which the design uses for these tiles too. */
const TILE_SIZE = '60px';
/** The hover scrim and marks of the tool page's example picker. FPL has no token for a scrim over media. */
const HOVER_SCRIM = 'rgba(0, 0, 0, 0.3)';
const ON_MEDIA_ICON = '#fff';
const GRID_COLUMNS = 4;

const styles = stylex.create({
  banner: {
    marginBottom: spacer.spacer2,
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacer.spacer2,
    paddingInline: spacer.spacer3,
    paddingTop: spacer.spacer1,
  },
  tooltipAnchor: {
    display: 'inline-flex',
  },
  dropTarget: {
    borderRadius: borderRadius.medium,
    display: 'flex',
  },
  dropOver: {
    outlineColor: color.borderSelected,
    outlineOffset: '2px',
    outlineStyle: 'solid',
    outlineWidth: '2px',
  },
  emptySlot: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderColor: color.border,
    borderRadius: borderRadius.medium,
    borderStyle: 'dashed',
    borderWidth: '1px',
    boxSizing: 'border-box',
    color: color.icon,
    cursor: { default: 'pointer', ':disabled': 'default' },
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer4px,
    height: TILE_SIZE,
    justifyContent: 'center',
    opacity: { default: 1, ':disabled': 0.4 },
    padding: 0,
    width: TILE_SIZE,
  },
  slotLabel: {
    whiteSpace: 'nowrap',
  },
  tile: {
    // the remove button reads this, so it shows while the pointer or focus is anywhere on the tile
    '--remove-opacity': { default: 0, ':hover': 1, ':focus-within': 1 },
    height: TILE_SIZE,
    position: 'relative',
    width: TILE_SIZE,
  },
  tileContent: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    display: 'block',
    // the thumbnail fills its parent, so without a set size it takes the shape of the result
    height: TILE_SIZE,
    padding: 0,
    position: 'relative',
    width: TILE_SIZE,
  },
  // the agent chat's failed attachment border
  tileFailed: {
    borderRadius: borderRadius.medium,
    outlineColor: color.borderDanger,
    outlineStyle: 'solid',
    outlineWidth: '1px',
  },
  localMedia: {
    backgroundColor: color.bgSecondary,
    borderRadius: borderRadius.medium,
    display: 'block',
    height: TILE_SIZE,
    objectFit: 'cover',
    width: TILE_SIZE,
  },
  localIcon: {
    alignItems: 'center',
    color: color.icon,
    display: 'flex',
    justifyContent: 'center',
  },
  // the agent chat's uploading attachment
  dimmed: {
    opacity: 0.4,
  },
  spinner: {
    alignItems: 'center',
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    pointerEvents: 'none',
    position: 'absolute',
  },
  caption: {
    backgroundColor: color.bg,
    borderRadius: borderRadius.small,
    bottom: spacer.spacer4px,
    color: color.text,
    left: spacer.spacer4px,
    paddingInline: spacer.spacer4px,
    pointerEvents: 'none',
    position: 'absolute',
    whiteSpace: 'nowrap',
  },
  remove: {
    alignItems: 'center',
    backgroundColor: color.bg,
    borderRadius: borderRadius.small,
    borderStyle: 'none',
    color: color.icon,
    cursor: 'pointer',
    display: 'flex',
    height: '16px',
    justifyContent: 'center',
    opacity: 'var(--remove-opacity)',
    padding: 0,
    position: 'absolute',
    right: spacer.spacer4px,
    top: spacer.spacer4px,
    width: '16px',
  },
  panel: {
    backgroundColor: color.bgMenu,
    borderRadius: borderRadius.large,
    boxShadow: shadow.elevation500,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  panelBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer2,
    padding: spacer.spacer3,
  },
  panelText: {
    margin: 0,
  },
  grid: {
    display: 'grid',
    gap: spacer.spacer4px,
    gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${TILE_SIZE})`,
    maxHeight: '256px',
    overflowY: 'auto',
  },
  option: {
    // the marks read this, so the empty circle shows while the pointer or focus is on the tile
    '--hover-opacity': { default: 0, ':hover': 1, ':focus-visible': 1 },
    backgroundColor: 'transparent',
    borderStyle: 'none',
    cursor: { default: 'pointer', ':disabled': 'default' },
    padding: 0,
    position: 'relative',
  },
  optionBlocked: {
    opacity: 0.4,
  },
  scrim: {
    backgroundColor: HOVER_SCRIM,
    borderRadius: borderRadius.medium,
    inset: 0,
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
  },
  scrimOnHover: {
    opacity: 'var(--hover-opacity)',
  },
  mark: {
    '--fpl-icon-color': ON_MEDIA_ICON,
    display: 'flex',
    filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.6))',
    pointerEvents: 'none',
    position: 'absolute',
    right: '1px',
    top: '1px',
  },
  markOnHover: {
    opacity: 'var(--hover-opacity)',
  },
  panelActions: {
    display: 'flex',
    gap: spacer.spacer2,
    padding: spacer.spacer3,
  },
  panelActionsDivided: {
    borderTopColor: color.border,
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
  },
});
